import moment from 'moment';
import { convertStatus } from './status';

/**
 * CSV column headers for shipment search export
 */
export const CSV_HEADERS = [
  'Shipment ID',
  'Internal ID',
  'Tracking Code',
  'Client ID',
  'Region',
  'Customer Name',
  'Street',
  'City',
  'State',
  'Zipcode',
  'Dropoff Window Start',
  'Dropoff Window End',
  'Label',
  'Status',
  'Delivered At',
];

/**
 * Escape a value for CSV format
 * - Coerces null/undefined to empty string
 * - Prefixes formula-like values (starting with =, +, -, @) with single quote to prevent Excel formula injection
 * - Escapes existing double quotes by doubling them
 * - Wraps in double quotes
 * @param {any} val - Value to escape
 * @returns {string} - CSV-escaped string
 */
export function escapeCsvValue(val) {
  // Coerce null/undefined to empty string
  let str = (val == null) ? '' : String(val);

  // Prevent Excel formula injection by prefixing with single quote
  if (str.length > 0 && ['=', '+', '-', '@'].includes(str[0])) {
    str = "'" + str;
  }

  // Escape double quotes and wrap in double quotes
  return `"${str.replace(/"/g, '""')}"`;
}

/**
 * Convert a single search result to a CSV row
 * @param {Object} result - Search result object containing shipment, doc, dropoff
 * @param {Object} statusAlias - Optional status alias mapping
 * @returns {string[]} - Array of CSV-escaped values
 */
export function resultToCsvRow(result, statusAlias = null) {
  const { shipment, doc, dropoff } = result;
  const address = shipment?.dropoff_address || {};

  const values = [
    shipment?.id || '',
    shipment?.internal_id || '',
    shipment?.tracking_code || '',
    shipment?.client_id || '',
    shipment?.region || '',
    shipment?.customer?.name || '',
    (address.street || '').replace(/,/g, ' '),
    address.city || '',
    address.state || '',
    address.zipcode || '',
    shipment?.dropoff_earliest_ts ? moment(shipment.dropoff_earliest_ts).format('MM/DD/YY hh:mm A') : '',
    shipment?.dropoff_latest_ts ? moment(shipment.dropoff_latest_ts).format('MM/DD/YY hh:mm A') : '',
    doc?.label || '',
    convertStatus(shipment?.status || '', statusAlias),
    dropoff?.actual_departure_ts ? moment(dropoff.actual_departure_ts).format('MM/DD/YY hh:mm A') : '',
  ];

  return values.map(escapeCsvValue);
}

/**
 * Generate CSV content from shipment search results
 * @param {Object[]} results - Array of search result objects
 * @param {Object} statusAlias - Optional status alias mapping
 * @returns {string} - CSV content string
 */
export function generateShipmentSearchCsv(results, statusAlias = null) {
  if (!results || results.length === 0) {
    return '';
  }

  const rows = results.map(result => resultToCsvRow(result, statusAlias));
  return [CSV_HEADERS.join(','), ...rows.map(row => row.join(','))].join('\n');
}

/**
 * Generate a timestamped filename for the CSV export
 * @returns {string} - Filename with timestamp
 */
export function generateCsvFilename() {
  const timestamp = moment().format('YYYY-MM-DD_HH-mm');
  return `shipment-search-${timestamp}.csv`;
}

